import { useSyncExternalStore } from 'react';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';

/**
 * Whose flags the Unleash client has fetched. The client starts without a patient and is told the
 * patient's id once the session has one (hooks/useUnleashContext). Until that fetch returns, a flag
 * rolled out by patient reads its anonymous value, which is off: right after a reload the AI
 * assistant reads as unavailable to a patient who has it. A caller that would act on "off" (drop
 * the patient's open chat, say) waits for this instead.
 */
let syncedUserId: string | null = null;
const listeners = new Set<() => void>();

/**
 * Called once the client's context update for `userId` has settled, or failed: either way the
 * flags are as final as they will get (see hooks/useUnleashContext for when that is).
 */
export const markUnleashFlagsSynced = (userId: string | null) => {
  syncedUserId = userId;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const getSyncedUserId = () => syncedUserId;

/** Whether the flags the client holds are this patient's. With no patient id yet there is no wait. */
export const useAreUnleashFlagsSynced = (): boolean => {
  const { userId } = useAppSelector(selectUser) ?? {};
  const synced = useSyncExternalStore(subscribe, getSyncedUserId);

  return !userId || synced === userId;
};
